import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { BASE_API_PATH, PHOTOS_PATH } from '../constants';

interface PhotoImage {
    id: string;
    imageUrl: string;
    caption: string;
    sortOrder: number;
    focalPointX?: number;
    focalPointY?: number;
}

interface PhotoCollectionDetail {
    id: string;
    title: string;
    description: string;
    category: string;
    location: string;
    publishedAt: number;
    photoCount: number;
    coverImageUrl: string;
    coverFocalPointX?: number;
    coverFocalPointY?: number;
    galleryImages: PhotoImage[];
}

function formatCollectionDate(collection: PhotoCollectionDetail) {
    if (collection.publishedAt > 0) {
        const date = new Date(collection.publishedAt * 1000);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }
    return 'Unknown date';
}

export function PhotoCollection() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { collectionId } = useParams();
    const [collection, setCollection] = useState<PhotoCollectionDetail | null>(null);
    const [loadingCollection, setLoadingCollection] = useState(true);
    const [collectionError, setCollectionError] = useState(false);
    const [activeImageIndex, setActiveImageIndex] = useState<number | null>(null);
    const [loadedImageKeys, setLoadedImageKeys] = useState<Record<string, boolean>>({});

    const getImageLoadKey = (image: PhotoImage, index: number) => `${image.id || 'image'}-${index}-${image.imageUrl}`;

    const markImageAsLoaded = (imageLoadKey: string) => {
        setLoadedImageKeys((previous) => (previous[imageLoadKey] ? previous : { ...previous, [imageLoadKey]: true }));
    };

    useEffect(() => {
        const fetchCollection = async () => {
            if (!collectionId) {
                setCollectionError(true);
                setLoadingCollection(false);
                return;
            }
            setLoadedImageKeys({});
            setActiveImageIndex(null);
            try {
                const response = await fetch(`${BASE_API_PATH}/photos/${collectionId}`);
                if (!response.ok) {
                    throw new Error('Could not fetch collection');
                }
                const data: PhotoCollectionDetail = await response.json();
                setCollection(data);
                setCollectionError(false);
                document.title = `${data.title} - Mike Brauninger`;
                ReactGA.send({ hitType: 'pageview', page: window.location.pathname });
            } catch (error) {
                setCollectionError(true);
                document.title = 'Photos - Mike Brauninger';
            } finally {
                setLoadingCollection(false);
            }
        };

        fetchCollection();
    }, [collectionId]);

    const sortedGalleryImages = useMemo(() => {
        if (!collection) {
            return [];
        }
        return [...collection.galleryImages].sort((left, right) => left.sortOrder - right.sortOrder);
    }, [collection]);
    const activeImage = activeImageIndex === null ? null : sortedGalleryImages[activeImageIndex] ?? null;
    const activeImageLoadKey =
        activeImage && activeImageIndex !== null ? getImageLoadKey(activeImage, activeImageIndex) : null;
    const isActiveImageLoaded = activeImageLoadKey ? Boolean(loadedImageKeys[activeImageLoadKey]) : false;

    useEffect(() => {
        if (isMobile) {
            setActiveImageIndex(null);
        }
    }, [isMobile]);

    if (loadingCollection) {
        return (
            <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 4, md: 6 } }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    <Typography sx={{ color: '#55729f' }}>Loading collection...</Typography>
                </Box>
            </Box>
        );
    }

    if (!collection || collectionError) {
        return (
            <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 4, md: 6 } }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    <Typography variant="h4" sx={{ color: '#163f86', fontWeight: 700, mb: 1 }}>
                        Collection not found
                    </Typography>
                    <Button component={Link} to={`/${PHOTOS_PATH}`} startIcon={<ArrowBackIcon />}>
                        Back to all collections
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <>
            <Helmet>
                <title>{collection.title} - Mike Brauninger</title>
            </Helmet>
            <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 4, md: 6 } }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.8}
                        sx={{
                            color: '#4d6f9f',
                            fontSize: 14,
                            mb: 2.25,
                            fontFamily: (theme) => theme.typography.fontFamily,
                        }}
                    >
                        <Link
                            to={`/${PHOTOS_PATH}`}
                            style={{
                                textDecoration: 'none',
                                color: '#2d5ea8',
                                display: 'flex',
                                fontWeight: 500,
                            }}
                        >
                            <ArrowBackIcon sx={{ fontSize: 15, mr: 0.5 }} />
                            <span>All Collections</span>
                        </Link>
                        <Typography component="span" sx={{ color: '#7f98be', fontSize: 14 }}>
                            /
                        </Typography>
                        <Typography
                            component="span"
                            aria-current="page"
                            sx={{ color: '#163f86', fontSize: 14, fontWeight: 700 }}
                        >
                            {collection.title}
                        </Typography>
                    </Stack>

                    <Box
                        sx={{
                            pb: 3,
                            borderBottom: '1px solid #d9e7f8',
                            mb: 2,
                        }}
                    >
                        <Typography variant="h3" sx={{ color: '#163f86', fontWeight: 700, mb: 1.2 }}>
                            {collection.title}
                        </Typography>
                        <Typography sx={{ color: '#55729f', maxWidth: 600, mb: 2.5 }}>{collection.description}</Typography>
                        <Stack
                            direction="row"
                            spacing={{ xs: 2, md: 3 }}
                            useFlexGap
                            sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CollectionsOutlinedIcon sx={{ color: '#2d66c3', fontSize: 21 }} />
                                <Box>
                                    <Typography sx={{ color: '#1f4f9c', fontWeight: 700, fontSize: 15 }}>
                                        {collection.photoCount}
                                    </Typography>
                                    <Typography sx={{ color: '#55729f', fontSize: 13 }}>Photos</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocationOnOutlinedIcon sx={{ color: '#2d66c3', fontSize: 21 }} />
                                <Box>
                                    <Typography sx={{ color: '#1f4f9c', fontWeight: 700, fontSize: 15 }}>
                                        {collection.location}
                                    </Typography>
                                    <Typography sx={{ color: '#55729f', fontSize: 13 }}>Location</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TodayOutlinedIcon sx={{ color: '#2d66c3', fontSize: 21 }} />
                                <Box>
                                    <Typography sx={{ color: '#1f4f9c', fontWeight: 700, fontSize: 15 }}>
                                        {formatCollectionDate(collection)}
                                    </Typography>
                                    <Typography sx={{ color: '#55729f', fontSize: 13 }}>Date</Typography>
                                </Box>
                            </Box>
                        </Stack>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
                            gap: 1.5,
                        }}
                    >
                        {sortedGalleryImages.map((image, index) => {
                            const imageLoadKey = getImageLoadKey(image, index);
                            const isLoaded = Boolean(loadedImageKeys[imageLoadKey]);
                            return (
                                <Box
                                    key={`${collection.id}-gallery-${image.id || index}`}
                                    onClick={isMobile ? undefined : () => setActiveImageIndex(index)}
                                    sx={{
                                        width: '100%',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        cursor: isMobile ? 'default' : 'pointer',
                                    }}
                                >
                                    <Box sx={{ position: 'relative', width: '100%', pt: '62.5%' }}>
                                        {!isLoaded && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    bgcolor: '#e7f0fd',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <CircularProgress size={28} sx={{ color: '#4f79b6' }} />
                                            </Box>
                                        )}
                                        <Box
                                            component="img"
                                            src={image.imageUrl}
                                            alt={`${collection.title} ${index + 1}`}
                                            onLoad={() => markImageAsLoaded(imageLoadKey)}
                                            onError={() => markImageAsLoaded(imageLoadKey)}
                                            sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                objectPosition: `${image.focalPointX ?? 50}% ${image.focalPointY ?? 50}%`,
                                                opacity: isLoaded ? 1 : 0,
                                                transition: 'opacity 160ms ease',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    <Dialog
                        open={!isMobile && activeImage !== null}
                        onClose={() => setActiveImageIndex(null)}
                        maxWidth="xl"
                        fullWidth
                        BackdropProps={{
                            sx: {
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            },
                        }}
                        PaperProps={{
                            sx: {
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                borderRadius: 2,
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
                            <IconButton aria-label="Close enlarged photo" onClick={() => setActiveImageIndex(null)}>
                                <CloseIcon sx={{ color: '#e8f2ff' }} />
                            </IconButton>
                        </Box>
                        {activeImage && (
                            <Box sx={{ px: { xs: 1.5, md: 2.5 }, pb: { xs: 1.5, md: 2.5 } }}>
                                <Box sx={{ position: 'relative', minHeight: { xs: '50vh', md: '70vh' } }}>
                                    {!isActiveImageLoaded && activeImageLoadKey && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                borderRadius: 1,
                                                bgcolor: '#e7f0fd',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <CircularProgress size={34} sx={{ color: '#4f79b6' }} />
                                        </Box>
                                    )}
                                    <Box
                                        component="img"
                                        src={activeImage.imageUrl}
                                        alt={activeImage.caption || `${collection.title} ${(activeImageIndex ?? 0) + 1}`}
                                        onLoad={() => {
                                            if (activeImageLoadKey) {
                                                markImageAsLoaded(activeImageLoadKey);
                                            }
                                        }}
                                        onError={() => {
                                            if (activeImageLoadKey) {
                                                markImageAsLoaded(activeImageLoadKey);
                                            }
                                        }}
                                        sx={{
                                            width: '100%',
                                            maxHeight: '80vh',
                                            objectFit: 'contain',
                                            borderRadius: 1,
                                            display: 'block',
                                            opacity: isActiveImageLoaded ? 1 : 0,
                                            transition: 'opacity 160ms ease',
                                        }}
                                    />
                                </Box>
                                {activeImage.caption && (
                                    <Typography sx={{ color: '#d2e3fb', mt: 1, fontSize: 14 }}>{activeImage.caption}</Typography>
                                )}
                            </Box>
                        )}
                    </Dialog>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.75 }}>
                        <Button
                            component={Link}
                            to={`/${PHOTOS_PATH}`}
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            sx={{
                                borderColor: '#9ebdf0',
                                color: '#1f4f9c',
                                '&:hover': {
                                    borderColor: '#7da8e8',
                                    backgroundColor: '#eaf3ff',
                                },
                            }}
                        >
                            Back to all collections
                        </Button>
                    </Box>
                </Box>
            </Box>
        </>
    );
}
